<template>
  <!-- The bidding area -->
  <div class="biddingArea paddingSmallTop">
    <!--
			Item selling status (can include):
			- Time until auction starts
			- Lots until item is being offered
			- Time left to bid on item
			- Whether a lot has sold (and for how much)
			- Whether a lot has passed
			- Whether the auction is paused
		-->
    <div>
      <!-- nice transitions when content changes -->
      <transition
        name="fade"
        mode="out-in"
      >
        <!-- the item has not passed nor has it sold -->
        <div
          v-if="!itemBiddingStatus.has_passed && !itemBiddingStatus.has_sold"
          :key="'not_sold'"
        >
          <!-- show the countdown clock -->
          <Countdown
            v-show="auctionStatus !== 'paused'"
            :item_id="item_id"
          />
        </div>

        <!-- the item has sold or passed -->
        <div
          v-else
          :key="'sold'"
        >
          <!-- the sold status of the item -->
          {{ itemSoldStatus }}
        </div>
      </transition>
      <!-- the auction is paused -->
      <div
        v-if="auctionStatus === 'paused'"
        :key="'paused'"
        class="colorError"
      >
        The auction is paused and will resume momentarily.
      </div>
    </div>

    <!--
			The BiddingStatus component is the status of a customer's bid on the item (can include):
			- Must sign in to bid (if they are not authenticated)
			- They have not placed a bid
			- They have placed a bid and are outbid
			- They have placed a bid and are the current high bidder
			- They have placed a maximum bid and are the current high bidder (with a maximum bid of...)
			- They are the winning bidder.
		-->
    <div
      v-if="auctionStatus !== 'paused'"
      class="marginSmallBottom marginSmallTop flexBox biddingArea-biddingStatus"
    >
      <BiddingStatus
        :item_id="item_id"
        :view_type="'item'"
      />

      <div
        v-if="phoneBidStatus"
        class="paddingNormalLeft marginSmallTop flagColorDefault"
      >
        <p v-html="phoneBidStatus" />
      </div>
    </div>

    <!--
			The bidding area (area in which all the bidding activitiy takes place, including:
			- The bid amount
			- The phone number (when placing telephone bid)
			- Agreement to terms
			- Entering of credit card
			- Bid confirmation
		-->
    <div
      class="marginNormalBottom"
      :class="{ transparent: (!itemBiddingStatus.is_bidding_open || auctionStatus === 'paused') && creditCardFormIsOpenInBiddingArea === false }"
    >
      <!-- a nice transition for when things change -->
      <transition
        name="fade"
        mode="out-in"
      >
        <!--
					There is no apiResponse from bidding so we are not in the middle of placing a bid so
					assume we are in the INITIAL stage of bidding.
				-->
        <div
          v-if="(typeof apiResponse.place_bid === 'undefined' && !creditCardFormIsOpenInBiddingArea) ||
            (apiResponse.place_bid.message_id === 'PLACE_BID_SUCCESS' && !creditCardFormIsOpenInBiddingArea) ||
            (apiResponse.place_bid.message_id === 'NOT_PROVIDED' && !creditCardFormIsOpenInBiddingArea) ||
            (apiResponse.place_bid.message_id === 'INITIAL' && !creditCardFormIsOpenInBiddingArea)"
          :key="'INITIAL'"
        >
          <!-- a nice transition when is bidding open status changes -->
          <transition
            name="fade"
            mode="out-in"
          >
            <!-- bidding is not open for this item -->
            <div
              v-if=" !itemBiddingStatus.is_bidding_open"
              :key="itemBiddingStatus.is_bidding_open"
            >
              Bidding has closed.
            </div>

            <!-- bidding is still open for this item -->
            <div
              v-else
              :key="'BIDDING_IS_OPEN'"
            >
              <div class="flexBoxSpread">
                <div class="fontLarge">
                  $
                </div>

                <label for="inputBidAmount" />

                <!-- we are unauthenticated or this is the current live lot -->
                <div
                  v-if="!isAuthenticated || itemBiddingStatus.current_live_lot"
                  class="fontLarge paddingNormalRight paddingSmallLeft flexGrow textRight"
                >
                  <!-- show the bid amount -->
                  <div class="">
                    {{ bidAmount }}
                  </div>

                  <!-- hide the bid amount input -->
                  <input
                    id="inputBidAmount"
                    ref="inputBidAmount"
                    v-model="bidAmount"
                    autocomplete="off"
                    type="text"
                    class="hidden"
                    placeholder=""
                  >
                </div>

                <!-- we are authenticated and this is not the current live lot -->
                <div
                  v-else
                  class="fontLarge paddingSmallRight paddingSmallLeft flexGrow"
                >
                  <!-- the bid amount can be changed -->
                  <input
                    id="inputBidAmount"
                    ref="inputBidAmount"
                    v-model="bidAmount"
                    onclick="this.setSelectionRange(0, this.value.length)"
                    autocomplete="off"
                    type="text"
                    class="textRight"
                    placeholder=""
                    required
                    @focus="disableBidUpdate = true"
                    @blur="disableBidUpdate = false"
                  >
                </div>

                <!-- The Bid Button -->
                <BidButton
                  ref="childBidButton"
                  :item_id="item_id"
                  :bid-amount="bidAmount"
                  :Card="Card"
                  @resetPhoneForm="resetPhoneForm"
                />
              </div>

              <!-- The Telephone Bidding Link -->
              <TelephoneButton
                v-if="itemBiddingStatus.is_telephone_bidding_open"
                :item_id="item_id"
                :primary-phone="primaryPhoneFormatted"
                :secondary-phone="secondaryPhoneFormatted"
                :Card="Card"
                @resetPhoneForm="resetPhoneForm"
              />
            </div>
          </transition>
        </div>

        <!-- REQUIRES_CREDIT_CARD -->
        <div
          v-else-if="apiResponse.place_bid.message_id === 'REQUIRES_CREDIT_CARD' || (showCreditCardFormModal || creditCardFormIsOpenInBiddingArea)"
          :key="apiResponse.place_bid.message_id"
        >
          <!--
						The stationary credit card form
						- should disallow auto advance lot when open
						- should send cc info to Braintree with hosted fields with addCard method
						- should allow customer to place the bid after add cc is succesful if that item is still current live lot
					-->
          <div
            v-if="showCreditCardFormModal || creditCardFormIsOpenInBiddingArea"
            class="paddingNormalBottom"
          >
            <CreditCardFormModal
              :message="typeof apiResponse.add_card === 'undefined' ? 'You must have a credit card on file to place a bid.' : apiResponse.add_card.message"
              :card-values="Card"
              @updateCard="setCard"
              @confirm="addCard"
              @close="closeCreditCardFormModal"
            />
          </div>

          <!-- we are including the cancel button -->
          <div class="bidButtonFullWidth">
            <!-- show the action button -->
            <div class="bidButtonActionButton">
              <button
                class="bidButton backgroundColorSuccess u-full-width"
                @click="openCreditCardFormModal"
              >
                Add Card
              </button>
            </div>

            <!-- show the cancel button -->
            <div class="bidButtonCancelButton">
              <span
                class="cancelButton"
                @click="resetForm"
              >Cancel</span>
            </div>
          </div>
        </div>

        <!-- REQUIRES_BID_AMOUNT -->
        <div
          v-else-if="apiResponse.place_bid.message_id === 'REQUIRES_BID_AMOUNT'"
          :key="'REQUIRES_BID_AMOUNT'"
        >
          <!-- display the bid amount input -->
          <div>
            <span>$</span>
            <input
              id="inputBidAmount"
              ref="inputBidAmount"
              v-model="bidAmount"
              type="text"
              class="textRight fontLarge"
              :disabled="itemBiddingStatus.current_live_lot"
              placeholder=""
              required
              @focus="disableBidUpdate = true"
              @blur="disableBidUpdate = false"
            >
          </div>

          <!-- display the bid button -->
          <BidButton
            :item_id="item_id"
            :bid-amount="bidAmount"
            :Card="Card"
          />
        </div>

        <!-- REQUIRES_PRIMARY_PHONE -->
        <div
          v-else-if="apiResponse.place_bid.message_id === 'REQUIRES_PRIMARY_PHONE' && itemBiddingStatus.is_telephone_bidding_open"
          :key="'REQUIRES_PRIMARY_PHONE'"
        >
          <!-- display the primary phone number input -->
          <div class="row">
            <div class="paddingSmallBottom">
              Primary number:
            </div>
            <div class="backgroundColorDimmer borderGrey">
              <VuePhoneNumberInput
                ref="primaryPhone"
                v-model="primaryPhone"
                :no-validator-state="true"
                color="#007aff"
                valid-color="#34c263"
                error-color="#f44242"
                :default-country-code="'US'"
                :border-radius="0"
                :translations="{ phoneNumberLabel: '' }"
                :no-example="true"
                @update="formatPrimaryPhone"
              />
            </div>
          </div>

          <!-- display the secondary phone number input -->
          <div class="row">
            <div class="paddingSmallBottom paddingNormalTop">
              Secondary number:
            </div>
            <div class="backgroundColorDimmer borderGrey">
              <VuePhoneNumberInput
                ref="secondaryPhone"
                v-model="secondaryPhone"
                :no-validator-state="true"
                color="#007aff"
                valid-color="#34c263"
                error-color="#f44242"
                :default-country-code="'US'"
                :border-radius="0"
                :translations="{ phoneNumberLabel: '' }"
                :no-example="true"
                @update="formatSecondaryPhone"
              />
            </div>
          </div>

          <!-- display the telephone bid button -->
          <TelephoneButton
            :item_id="item_id"
            :primary-phone="primaryPhoneFormatted"
            :secondary-phone="secondaryPhoneFormatted"
            :show-cancel-button="true"
            :Card="Card"
            @resetPhoneForm="resetPhoneForm"
          />
        </div>

        <!-- REQUIRES_AGREEMENT_TO_TERMS -->
        <div
          v-else-if="apiResponse.place_bid.message_id === 'REQUIRES_AGREEMENT_TO_TERMS'"
          :key="'REQUIRES_AGREEMENT_TO_TERMS'"
        >
          <!-- display the agreement to terms text -->
          <div>
            <p>
              By clicking "I Agree," you acknowledge that you have read, understand, and agree to the <a
                :href="item.auction_url + 'details/'"
                target="_blank"
              >Terms &amp; Conditions</a> for this auction.
            </p>
            <p v-if="customer.international">
              Please note that <span class="bold">international bidders are required to pay by wire transfer</span> for all winning bids within five (5) days of the close of the auction.
            </p>
          </div>

          <!-- this is not a telephone bid.  display the bid button -->
          <BidButton
            v-if="!itemIsTelephoneBid"
            :item_id="item_id"
            :bid-amount="bidAmount"
            :show-cancel-button="true"
            :Card="Card"
            @resetForm="resetForm"
          />

          <!-- this is a telephone bid.  display the telephone bid button -->
          <BidButton
            v-if="itemIsTelephoneBid"
            :item_id="item_id"
            :bid-amount="bidAmount"
            :show-cancel-button="true"
            :primary-phone="primaryPhoneFormatted"
            :secondary-phone="secondaryPhoneFormatted"
            :Card="Card"
            @resetForm="resetForm"
          />

          <!--<TelephoneButton v-else-if="itemBiddingStatus.is_telephone_bidding_open" :item_id="item_id" :primaryPhone="primaryPhoneFormatted" :secondaryPhone="secondaryPhoneFormatted" :showCancelButton="true" :Card="this.Card" @resetPhoneForm="resetPhoneForm"/>-->
        </div>

        <!-- REQUIRES_AMOUNT_CONFIRMATION -->
        <div
          v-else-if="apiResponse.place_bid.message_id === 'REQUIRES_AMOUNT_CONFIRMATION'"
          :key="'REQUIRES_AMOUNT_CONFIRMATION'"
        >
          <!-- this is not a telephone bid.  show the bid button -->
          <BidButton
            v-if="!itemIsTelephoneBid"
            :item_id="item_id"
            :bid-amount="bidAmount"
            :show-cancel-button="true"
            :Card="Card"
            @resetForm="resetForm"
          />

          <BidButton
            v-if="itemIsTelephoneBid"
            :item_id="item_id"
            :bid-amount="bidAmount"
            :show-cancel-button="true"
            :primary-phone="primaryPhoneFormatted"
            :secondary-phone="secondaryPhoneFormatted"
            :Card="Card"
            @resetForm="resetForm"
          />

          <!-- telephone bidding is open.  display the telephone bid button -->
          <!--<TelephoneButton v-else-if="itemBiddingStatus.is_telephone_bidding_open" :item_id="item_id" :primaryPhone="primaryPhoneFormatted" :secondaryPhone="secondaryPhoneFormatted" :showCancelButton="true" :Card="this.Card" @resetPhoneForm="resetPhoneForm"/> -->
        </div>
      </transition>
    </div>

    <!--
			Informational area for bid limits and credit card requirements.
		-->
    <div
      class="marginNormalBottom"
      :class="{ transparent: !itemBiddingStatus.is_bidding_open }"
    >
      <!-- a fade transition for when the content in the bidding area changes -->
      <transition
        name="fade"
        mode="out-in"
      >
        <!-- we are in the inital state of placing a bid -->
        <div
          v-if="typeof apiResponse.place_bid === 'undefined'"
          :key="1"
        >
          <!-- we are authenticated and have a bid limit and the auction does not ignore bid limits -->
          <div
            v-if="isAuthenticated && customer.bid_limit > 0 && !itemBiddingStatus.auction_ignore_bid_limit"
            class="paddingSmallTop"
          >
            <div v-if="isLoadingCustomerBidLimit">
              <div class="textCenter">
                <i class="fa fa-spinner fa-spin fa-1x fa-fw" />
                <span class="sr-only">Loading...</span>
              </div>
            </div>
            <div v-else>
              <!-- display our bid limit -->
              The current bid limit for this account is ${{ (customerBidLimit).toString().replace(/\d(?=(\d{3})+$)/g, '$&,') }}.  Learn more about <a href="/bid_limits.html">raising bid limits</a>.
            </div>
          </div>

          <!-- the customer requires a credit card to bid -->
          <div
            v-if="customer.requires_credit_card"
            class="paddingSmallTop understatedText"
          >
            <!-- show the link to add a credit card -->
            A credit card is required to place a bid.  <a
              href="#"
              @click="bid"
            >Add a credit card now</a>.
          </div>
        </div>

        <!-- we are not in the initial state of bidding -->
        <div
          v-else
          :key="2"
        >
          <!-- display the response to our bid from the server -->
          <ApiResponse
            :context="'place_bid'"
            :details="true"
          />
        </div>
      </transition>
    </div>

    <!--
			Bidding History for the item
		-->
    <div>
      <BiddingMessages
        v-if="!itemBiddingStatus.has_sold && !itemBiddingStatus.has_passed"
        :item_id="item_id"
      />
    </div>
  </div>
</template>

<script>
import ApiResponse from '../components/ApiResponse'
import Countdown from '../components/Countdown'
import CreditCardFormModal from '../components/Forms/CreditCardFormModal'
import BidButton from '../components/BidButton'
import BiddingMessages from '../components/BiddingMessages'
import BiddingStatus from '../components/BiddingStatus'
import TelephoneButton from '../components/TelephoneButton'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import LiveModeMixin from '../mixins/LiveModeMixin'

export default {
	name: 'BiddingArea',
	components: {
		ApiResponse,
		BidButton,
		BiddingStatus,
		BiddingMessages,
		Countdown,
		CreditCardFormModal,
		TelephoneButton,
		VuePhoneNumberInput
	},
	mixins: [LiveModeMixin],
	props: [
		'item_id'
	],
	data () {
		return {
			'soldStatus': '',
			'disableBidUpdate': false,
			'bidAmount': null,
			'isTelephoneBid': false,
			'primaryPhone': '',
			'primaryPhoneFormatted': '',
			'secondaryPhone': '',
			'secondaryPhoneFormatted': '',
			'Card': {
				'card_number': '',
				'expire_month': '',
				'expire_year': '',
				'cvv2': '',
				'billing_address': '',
				'billing_zip': ''
			},
			'showCreditCardFormModal': false
		}
	},

	computed: {

		itemBidButtonAmount: function () {
			return this.$store.getters.itemBidButtonAmount(this.item_id)
		},

		apiResponse: function () {
			return this.$store.getters.apiResponse
		},

		itemBiddingStatus: function () {
			return this.$store.getters.itemBiddingStatus(this.item_id)
		},
		customerMaxBid: function () {
			return this.$store.getters.customerMaxBid(this.item_id)
		},

		isAuthenticated: function () {
			return this.$store.getters.isAuthenticated
		},

		itemSoldStatus: function () {
			return this.$store.getters.itemSoldStatus(this.item_id)
		},

		placeBidAdjustedAmount: function () {
			return this.$store.getters.placeBidAdjustedAmount
		},

		item: function () {
			return this.$store.getters.item(this.item_id)
		},

		customer: function () {
			return this.$store.getters.customer
		},
		customerBidLimit: function () {
			if (this.customerComputedBidLimit && this.customerComputedBidLimit.customer_computed_bid_limit !== undefined) {
				return this.customerComputedBidLimit.customer_computed_bid_limit
			}
			return this.customer.bid_limit
		},
		auctionStatus: function () {
			return this.$store.getters.auctionStatus(this.item_id)
		},
		creditCardFormIsOpenInBiddingArea: function () {
			return this.$store.getters.creditCardFormIsOpenInBiddingArea
		},
		currentLiveInventoryId: function () {
			return this.$store.getters.currentLiveInventoryId
		},
		liveMode: function () {
			return this.$store.getters.liveMode
		},
		phoneBidStatus: function () {
			if (this.$store.getters.customerPhoneBids(this.item_id)) {
				let message = 'You are currently scheduled for telephone bidding on this lot at the following number: <br>'

				if (this.$store.getters.customerPhoneBids(this.item_id).primaryphone !== '' && this.$store.getters.customerPhoneBids(this.item_id).secondaryphone !== '') {
					message = 'You are currently scheduled for telephone bidding on this lot at the following numbers: <br>'
					message += this.$store.getters.customerPhoneBids(this.item_id).primaryphone
					message += '<br>'
					message += this.$store.getters.customerPhoneBids(this.item_id).secondaryphone
					return message
				} else {
					message += this.$store.getters.customerPhoneBids(this.item_id).primaryphone
					return message
				}
			}

			return ''
		},
		isLoadingCustomerBidLimit: function () {
			return this.$store.getters.customerComputedBidLimit.isLoading
		},
		customerComputedBidLimit: function () {
			return this.$store.getters.customerComputedBidLimit
		}
	},

	watch: {

		'placeBidAdjustedAmount': function () {
			if (this.placeBidAdjustedAmount) {
				// set the bid button amount
				this.resetBidButtonAmount()
			}
		},

		'customerMaxBid.next_increment': function () {
			// set the bid button amount and the api response
			this.resetBidButtonAmount()
			this.resetApiResponse()
		},

		'itemBiddingStatus.current_asking_bid': function () {
			// set the bid button amount and the api response
			this.resetBidButtonAmount()
			this.resetApiResponse()
			if (this.isAuthenticated) {
				this.getCustomerComputedBidLimit()
			}
		},
		'isAuthenticated': function () {
			if (this.isAuthenticated) {
				this.getCustomerComputedBidLimit()
			}
		}
	},

	mounted () {
		this.resetBidButtonAmount()
		this.resetApiResponse()
		this.$store.dispatch('getItems', {item_ids: [this.item_id]})
	},

	methods: {
		/**
     * Gets called when user must add credit card to bid and clicks Add Card button
     *
     * @public
     */
		openCreditCardFormModal: function () {
			this.showCreditCardFormModal = true

			// This will prevent the lot from advancing while cc form is open
			this.$store.dispatch('setCreditCardFormIsOpenInBiddingArea', true)
		},
		/**
     * Gets called when user closes credit card form
     *
     * @public
     */
		closeCreditCardFormModal: function () {
			this.showCreditCardFormModal = false
			// This will allow auto advance to next lot when cc form is closed by user
			this.$store.dispatch('setCreditCardFormIsOpenInBiddingArea', false)
			// We have closed the cc form, if the current item is not the live item, we must go to live item
			if (this.liveMode && this.currentLiveInventoryId && this.item_id !== this.currentLiveInventoryId) {
				// We pass false so we do not delay the transition by 3 seconds
				this.goToLiveLot(this.currentLiveInventoryId, false)
			}
		},
		getCustomerComputedBidLimit: function () {
			return this.$store.dispatch('getCustomerComputedBidLimit', { inventory_id: this.item_id })
		},
		estimateArta: function () {},
		resetForm: function () {
			this.disableBidUpdate = false
			this.resetBidButtonAmount()
			this.resetApiResponse()
		},
		/**
     * Gets called when user completes credit card form and clicks Add Card
     *
     * @public
     */
		addCard: function () {
			// Card was successfully added, refresh the customer account
			this.$store.dispatch('getAccount')
			// Reset the bidding area
			this.resetForm()
			// Close the credit card form modal
			this.closeCreditCardFormModal()
		},
		formatPrimaryPhone: function (value) {
			this.primaryPhoneFormatted = value.formatInternational
		},

		formatSecondaryPhone: function (value) {
			this.secondaryPhoneFormatted = value.formatInternational
		},

		bid: function () {
			this.$refs.childBidButton.placeBid()
		},

		computePhoneBidStarted: function () {
			if (!this.itemBiddingStatus.phone_bid_started) {
				this.isTelephoneBid = false
			} else {
				this.isTelephoneBid = this.itemBiddingStatus.phone_bid_started
			}
		},

		itemIsTelephoneBid: function () {
			return this.$store.getters.itemIsTelephoneBid(this.item_id)
		},

		resetPhoneForm: function () {
			this.primaryPhone = ''
			this.secondaryPhone = ''
			this.primaryPhoneFormatted = ''
			this.secondaryPhoneFormatted = ''
		},

		setCard: function (cardValues) {
			this.Card.card_number = cardValues.card_number
		},

		resetApiResponse: function () {
			if (this.disableBidUpdate === false) {
				// clear the last api response
				this.$store.dispatch('clearApiResponse', 'place_bid')
			}
		},

		resetBidButtonAmount: function () {
			if (this.disableBidUpdate === false) {
				let nextIncrement = 0
				if (this.customerMaxBid) {
					nextIncrement = this.customerMaxBid.next_increment
				}

				let currentAskingBid = 0
				if (this.itemBiddingStatus.current_asking_bid !== '') {
					currentAskingBid = this.itemBiddingStatus.current_asking_bid
				}
				let computedBidAmount = 0
				if (this.itemBidButtonAmount > 0) {
					computedBidAmount = this.itemBidButtonAmount
				}

				this.bidAmount = (Math.max(nextIncrement, currentAskingBid, computedBidAmount)).toString().replace(/\d(?=(\d{3})+$)/g, '$&,')
			}
		}
	}
}
</script>

<style scoped>
</style>
